import React, { useCallback, useState } from 'react'
import {
  BtButton,
  BtGoogleAutocomplete,
  BtInput,
  BtPhoneInput,
  BtRadioGroup,
} from '@linqpal/components/src/ui'
import { useTranslation } from 'react-i18next'
import { PropertyOwnerModel as Model } from '@linqpal/models'
import { Text, View } from 'react-native'
import {
  validateUSPhoneNumber,
  validateZip,
} from '@linqpal/models/src/helpers/validations'
import { PROPERTY_OWNER_TYPE } from '@linqpal/models/src/dictionaries'
import { AddOwner, City, State, Zip } from '../components'
import ProjectStore from '../Store'

const Details = ({ model, value }) => {
  const { t } = useTranslation('global')
  const { isBusiness, addAnotherOwner, removeOwner } = ProjectStore
  const [expanded, setExpanded] = useState<string>('')
  const validateInput = useCallback(
    (val: string) =>
      validateUSPhoneNumber(val) ? '' : t('ProjectForm.phone-invalid'),
    [t],
  )

  const getFullAddress = (addr) => {
    const { address, city, state, zip } = addr ?? {}
    if (address && city && state && zip)
      return `${address} ${city} ${state} ${zip}`
    else return ''
  }

  const addMore = () => addAnotherOwner(value)
  const options = [
    {
      label: t('ProjectForm.individual-owner'),
      value: PROPERTY_OWNER_TYPE.INDIVIDUAL,
    },
    { label: t('ProjectForm.business'), value: PROPERTY_OWNER_TYPE.BUSINESS },
  ]

  return (
    <>
      {value.map((v, index) => {
        const { id, type, businessName, firstName, lastName, phone, address } =
          v
        return (
          <React.Fragment key={id}>
            {index !== 0 && (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text
                  style={{
                    fontWeight: '700',
                    fontSize: 20,
                    marginTop: 30,
                    marginBottom: 10,
                  }}
                >
                  {t('ProjectForm.another-owner')}
                </Text>
                <BtButton
                  size="small"
                  appearance={'ghost'}
                  status={'basic'}
                  onPress={() => removeOwner(value, id)}
                  style={{ marginTop: 20 }}
                  // eslint-disable-next-line i18next/no-literal-string
                  iconLeft="trash-2-outline"
                >
                  {t('ProjectForm.delete')}
                </BtButton>
              </View>
            )}
            <BtRadioGroup
              value={type}
              onChange={(val) => model.setMultipleValue('type', val, id)}
              options={options}
              radioStyle={{ marginRight: 32 }}
              groupStyle={{ flexDirection: 'row' }}
              labelStyle={{ color: '#19262F', fontWeight: '500', fontSize: 16 }}
              testID="propertyOwnerType"
            />
            {isBusiness(v) && (
              <BtInput
                value={businessName}
                onChangeText={(val) =>
                  model.setMultipleValue('businessName', val, id)
                }
                testID="propertyOwnerBusinessName"
                label={t('ProjectForm.business-name-label')}
                style={{ marginTop: 20 }}
              />
            )}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                marginTop: 20,
              }}
            >
              <BtInput
                value={firstName}
                onChangeText={(val) =>
                  model.setMultipleValue('firstName', val, id)
                }
                testID="propertyOwnerFirstname"
                label={t('ProjectForm.first-name-label')}
                style={{ width: '48%' }}
              />
              <BtInput
                value={lastName}
                onChangeText={(val) =>
                  model.setMultipleValue('lastName', val, id)
                }
                testID="propertyOwnerLastname"
                label={t('ProjectForm.last-name-label')}
                style={{ width: '48%' }}
              />
            </View>
            <BtPhoneInput
              value={phone}
              testID="contractorPhone"
              label={
                isBusiness(v)
                  ? t('ProjectForm.business-phone')
                  : t('ProjectForm.phone-number')
              }
              validate={validateInput}
              onChangeText={(val) =>
                model.setMultipleValue('phone', val.parsed, id)
              }
              style={{ marginTop: 20 }}
            />
            <BtGoogleAutocomplete
              value={getFullAddress(address)}
              style={{ marginTop: 20 }}
              onSelect={(val) => {
                model.setMultipleValue('address', val, id)
                if (validateZip(val.zip) && val.city) setExpanded('')
              }}
              onChange={(val: string) =>
                model.setMultipleValue(
                  'address',
                  {
                    ...address,
                    address: val,
                  },
                  id,
                )
              }
              label={
                isBusiness(v)
                  ? t('ProjectForm.address-label')
                  : t('ProjectForm.home-address')
              }
              onFocus={() => {
                setExpanded(id)
              }}
            />
            {expanded === id && (
              <>
                <State
                  value={address?.state}
                  onChangeText={(val) =>
                    model.setMultipleValue(
                      'address',
                      {
                        ...address,
                        state: val,
                      },
                      id,
                    )
                  }
                />
                <City
                  value={address?.city}
                  onChangeText={(val) =>
                    model.setMultipleValue(
                      'address',
                      {
                        ...address,
                        city: val ?? '',
                      },
                      id,
                    )
                  }
                  style={{ marginTop: 20 }}
                />
                <Zip
                  value={address?.zip}
                  onChangeText={(val) =>
                    model.setMultipleValue(
                      'address',
                      {
                        ...address,
                        zip: val ?? '',
                      },
                      id,
                    )
                  }
                  style={{ marginTop: 20 }}
                />
              </>
            )}
            {index === 0 && <AddOwner onPress={addMore} />}
          </React.Fragment>
        )
      })}
    </>
  )
}

export default {
  identifier: 'details',
  title: '',
  component: Details,
  Model,
  multipleAnswers: true,
  optional: true,
}
